import type { supervision, supervisionBookings } from '@cwos/api-client';
import type { Severity } from '@cwos/ui-shared';

/**
 * Pure presentation + form logic for supervision BOOKINGS.
 *
 * ===========================================================================
 * NOTHING HERE DECIDES WHETHER SUPERVISION HAPPENED
 * ===========================================================================
 * The reading of a row is `supervisionBookings.supervisionMoment`, which both
 * apps share — an admin and the worker they supervise must not be shown two
 * different accounts of the same booking. This file only decides how that
 * reading LOOKS on the admin console: which pill, which heading a row sits
 * under, and what a booking form needs before it can be submitted.
 *
 * The one thing it deliberately does NOT hold is a cadence. An expected
 * supervision interval is applicability-driven operational data with
 * provenance, scope and effective dates, resolved server-side
 * (`apps/api/src/domains/expectation/`), and where none can be established the
 * answer is `insufficient_context` rather than an invented number. A booking
 * form that proposed "next one in 45 days" would re-introduce exactly the
 * default that was deleted. See {@link nextDueSentence}.
 */

/**
 * Pill colour per booking reading.
 *
 * `awaiting_outcome` is `urgent` and `booked_ahead` is `sky`, and the gap
 * between them is the whole point: they are the SAME status word on the wire
 * (`scheduled`) and they are opposite facts. One is a plan that is still fine;
 * the other is a supervision slot that went by with nobody accounting for it.
 *
 * `unconfirmed` is `unsure` rather than `good`: the meeting happened and no
 * supervisor has attested, so the answer is not established.
 *
 * `recorded_unbooked` is `good`, the same as `confirmed_from_booking`.
 * SUPERVISION THAT HAPPENED WITHOUT A BOOKING IS NOT A DEFECT — colouring it
 * anything else would teach supervisors to book meetings retroactively so the
 * screen would stop complaining, which is the calendar manufacturing evidence
 * by a slower route.
 */
export const MOMENT_VARIANT: Record<supervisionBookings.SupervisionMomentKind, Severity> = {
  booked_ahead: 'sky',
  awaiting_outcome: 'urgent',
  unconfirmed: 'unsure',
  confirmed_from_booking: 'good',
  recorded_unbooked: 'good',
  cancelled: 'plain',
  no_show: 'urgent',
  corrected: 'plain',
  voided: 'unsure',
};

/**
 * WHICH INSTANT A ROW IS ENTITLED TO SHOW, in words rather than as a bare date.
 *
 * A bare timestamp in a column headed "When" is the collapse in its cheapest
 * form: `occurred_at` on a booking is the BOOKED instant (the column is NOT
 * NULL, so `insertBooking` fills it with the plan), and printed without a verb
 * it reads as a time supervision took place. The prefix comes from
 * `moment.sense`, which is derived from the status and never from the presence
 * of a timestamp.
 */
export function whenSentence(
  moment: supervisionBookings.SupervisionMoment,
  formatted: string,
): string {
  return moment.sense === 'booked_for' ? `Booked for ${formatted}` : formatted;
}

/**
 * The heading a booking sits under, or null for a row that belongs in history.
 *
 * Three groups, in the order a supervisor needs them: what is unaccounted for,
 * what happened and needs attesting, what is still to come. A row that has
 * reached a terminal state is history and is NOT repeated here — the session
 * table below already carries it, and showing it twice is how a screen invites
 * somebody to confirm a cancelled booking.
 */
export function bookingGroup(
  moment: supervisionBookings.SupervisionMoment,
): 'unaccounted' | 'to_confirm' | 'upcoming' | null {
  switch (moment.kind) {
    case 'awaiting_outcome':
      return 'unaccounted';
    case 'unconfirmed':
      return 'to_confirm';
    case 'booked_ahead':
      return 'upcoming';
    default:
      return null;
  }
}

export const GROUP_HEADING: Record<'unaccounted' | 'to_confirm' | 'upcoming', string> = {
  unaccounted: 'Booked time passed, nobody has said what happened',
  to_confirm: 'Happened — waiting on the supervisor to confirm',
  upcoming: 'Still to come',
};

/**
 * WHAT TO SAY ABOUT WHEN THE NEXT SUPERVISION IS DUE.
 *
 * `nextDueOn` is null in three genuinely different situations and this refuses
 * to give them one sentence:
 *
 *   - `insufficient_context` — NO EXPECTATION COULD BE ESTABLISHED. Not "none
 *     is owed", not a guessed cadence. The organization has declared nothing
 *     that reaches this worker, and the honest answer is to say so and ask for
 *     the record to be completed. Inventing a date here is the exact default
 *     (`SUPERVISION_GAP_DEFAULT_DAYS = 45`) that was deleted from the engine.
 *   - `not_applicable` — a requirement exists and excludes this worker.
 *   - anything else with no date — the server has a verdict but no next
 *     boundary, and its own `statusMeaning` is the sentence to show.
 *
 * The server sends `statusLabel` and `statusMeaning` beside every status for
 * exactly this reason, so nothing here composes copy for a status key it does
 * not recognise.
 */
export function nextDueSentence(
  worker: Pick<supervision.WorkerComplianceView, 'status' | 'statusMeaning' | 'nextDueOn'>,
  formatDate: (iso: string) => string,
): { kind: 'due_on' | 'no_expectation' | 'not_applicable' | 'stated'; text: string } {
  if (worker.status === 'insufficient_context') {
    return { kind: 'no_expectation', text: worker.statusMeaning };
  }
  if (worker.status === 'not_applicable') {
    return { kind: 'not_applicable', text: worker.statusMeaning };
  }
  if (worker.nextDueOn) {
    return { kind: 'due_on', text: `Next supervision due ${formatDate(worker.nextDueOn)}` };
  }
  return { kind: 'stated', text: worker.statusMeaning };
}

/**
 * MAY A BOOKING BE OFFERED AT ALL for this worker?
 *
 * A booking names a supervisor, and `bookSession` refuses a non-manager who
 * names anybody but themselves. So for a worker with no assigned supervisor a
 * non-manager has nobody to book as, and the button would open a form whose
 * only possible submission is a 403.
 *
 * It is NOT gated on the compliance status. Booking supervision for a worker
 * whose expectation cannot be established is perfectly reasonable — the
 * supervision is real whether or not a policy has been written down — and
 * refusing it would make a gap in the policy record into a gap in supervision.
 */
export function mayOfferBooking(input: {
  canRecord: boolean;
  managesOrgWide: boolean;
  assignedSupervisorWorkerId: string | null;
}): boolean {
  if (!input.canRecord) return false;
  if (input.managesOrgWide) return true;
  return input.assignedSupervisorWorkerId !== null;
}

// --- Booking draft ----------------------------------------------------------

export interface BookingDraft {
  /** `datetime-local`, in the viewer's own zone — the picker is local. */
  scheduledFor: string;
  durationMinutes: string;
  format: NonNullable<supervision.RecordSessionInput['format']>;
  requirementId: string;
  calendarEventRef: string;
}

export function emptyBookingDraft(defaultLocalInput: string): BookingDraft {
  return {
    scheduledFor: defaultLocalInput,
    durationMinutes: '60',
    format: 'in_person',
    requirementId: '',
    calendarEventRef: '',
  };
}

/**
 * THE MIRROR OF THE SESSION RULE, AND IT POINTS THE OTHER WAY.
 *
 * `validateSessionDraft` refuses a FUTURE date — "supervision cannot be
 * recorded before it has happened". This refuses a PAST one, with the server's
 * own sentence, because a booking is a claim about the future and back-dating
 * one is how "I have it booked" becomes "it happened".
 *
 * `nowIso` is a parameter so the boundary is testable without mocking a clock.
 */
export function validateBookingDraft(draft: BookingDraft, nowIso: string): Record<string, string> {
  const errors: Record<string, string> = {};
  if (draft.scheduledFor.trim() === '') {
    errors['scheduledFor'] = 'Choose when this supervision is booked for.';
  } else {
    const at = new Date(draft.scheduledFor);
    if (Number.isNaN(at.getTime())) {
      errors['scheduledFor'] = 'That date and time is not valid.';
    } else if (at.getTime() < new Date(nowIso).getTime() - 60_000) {
      errors['scheduledFor'] =
        'A supervision booking must be in the future. To record supervision that has already ' +
        'happened, record it directly.';
    }
  }
  const minutes = Number(draft.durationMinutes);
  if (!Number.isInteger(minutes) || minutes < 1 || minutes > 1440) {
    errors['durationMinutes'] = 'Enter a whole number of minutes, 1–1,440.';
  }
  if (draft.calendarEventRef.trim().length > 256) {
    errors['calendarEventRef'] = 'That calendar reference is too long (256 characters).';
  }
  return errors;
}

export function bookingPayload(
  workerId: string,
  supervisorWorkerId: string,
  draft: BookingDraft,
): supervisionBookings.BookSupervisionInput {
  return {
    workerId,
    supervisorWorkerId,
    requirementId: draft.requirementId || null,
    scheduledFor: new Date(draft.scheduledFor).toISOString(),
    durationMinutes: Number(draft.durationMinutes),
    format: draft.format,
    // Empty is NULL rather than an empty string: "no calendar" and "a calendar
    // reference that is the empty string" are different, and the server's
    // validator rejects the latter (min(1)).
    calendarEventRef: draft.calendarEventRef.trim() || null,
  };
}

// --- Confirming a booking ---------------------------------------------------

export interface ConfirmDraft {
  /** `datetime-local` — WHEN IT ACTUALLY HAPPENED, not when it was booked. */
  occurredAt: string;
  durationMinutes: string;
}

/**
 * A CONFIRMATION IS AN ATTESTATION, NOT AN ACKNOWLEDGEMENT OF THE CALENDAR.
 *
 * The booked instant and the booked duration are the DEFAULTS here and nothing
 * more. A one-press Confirm that copied the plan into the record would be the
 * capability's own defect implemented as a button: the supervisor would be
 * attesting to a time and a length they never stated, taken from the calendar
 * entry, which is exactly "a calendar event is supervision completion" with an
 * extra click. The form is prefilled because prefilling is a courtesy and it is
 * editable because the fields are the claim.
 */
export function confirmDraftFrom(row: {
  occurredAt: string;
  durationMinutes: number;
  scheduledFor: string | null;
  toLocalInput: (iso: string) => string;
}): ConfirmDraft {
  return {
    occurredAt: row.toLocalInput(row.scheduledFor ?? row.occurredAt),
    durationMinutes: String(row.durationMinutes),
  };
}

/**
 * The SAME ceiling `validateSessionDraft` applies, and for the same reason:
 * "supervision cannot be recorded before it has happened". Confirming a slot
 * before it has passed is precisely how "I have it booked" becomes "it
 * happened", and the server re-checks it regardless — this is the courtesy
 * copy, not the guard.
 */
export function validateConfirmDraft(draft: ConfirmDraft, nowIso: string): Record<string, string> {
  const errors: Record<string, string> = {};
  if (draft.occurredAt.trim() === '') {
    errors['occurredAt'] = 'Say when this supervision actually happened.';
  } else {
    const at = new Date(draft.occurredAt);
    if (Number.isNaN(at.getTime())) {
      errors['occurredAt'] = 'That date and time is not valid.';
    } else if (at.getTime() > new Date(nowIso).getTime() + 60_000) {
      errors['occurredAt'] = 'Supervision cannot be recorded before it has happened.';
    }
  }
  const minutes = Number(draft.durationMinutes);
  if (!Number.isInteger(minutes) || minutes < 1 || minutes > 1440) {
    errors['durationMinutes'] = 'Enter a whole number of minutes, 1\u20131,440.';
  }
  return errors;
}

export function confirmPayload(draft: ConfirmDraft): {
  occurredAt: string;
  durationMinutes: number;
} {
  return {
    occurredAt: new Date(draft.occurredAt).toISOString(),
    durationMinutes: Number(draft.durationMinutes),
  };
}

// --- Closing a booking ------------------------------------------------------

export interface CloseDraft {
  outcome: 'cancelled' | 'no_show';
  reason: string;
}

export const EMPTY_CLOSE_DRAFT: CloseDraft = { outcome: 'cancelled', reason: '' };

/**
 * Closing a booking must say why, and the server enforces it. The two outcomes
 * are DIFFERENT FACTS — a meeting called off in advance and a meeting nobody
 * came to — and an organization looking at a pattern of no-shows needs to be
 * able to see it as one, which is why neither is spelled 'voided'.
 */
export function validateCloseDraft(draft: CloseDraft): Record<string, string> {
  const errors: Record<string, string> = {};
  const reason = draft.reason.trim();
  if (reason.length === 0) errors['reason'] = 'Say why this booking is being closed.';
  else if (reason.length > 500) errors['reason'] = 'Keep this under 500 characters.';
  return errors;
}

export const CLOSE_OUTCOME_LABELS: Record<CloseDraft['outcome'], string> = {
  cancelled: 'Cancelled before it happened',
  no_show: 'Nobody came',
};

/**
 * The sentence a viewer who may NOT confirm is shown in place of the button.
 *
 * Not a hidden control. `assertMayConfirm` refuses organization-wide management
 * on purpose — confirming is attesting, and a manager who was not in the room
 * cannot attest — and a manager who simply finds no button will conclude the
 * product is broken and go looking for another way. Saying it is a policy, and
 * naming who CAN, is the difference between a refusal and a dead end.
 */
export function confirmRefusalSentence(supervisorName: string | null): string {
  const who = supervisorName ?? 'the supervisor who delivered it';
  return (
    `Only ${who} can confirm this happened. Confirming is attesting that you were in the room, ` +
    'so it is not something an administrator can do on their behalf.'
  );
}
